// Folders Context for folder management
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { 
    getFolders, 
    getFolder, 
    createFolder as createFolderFirestore, 
    updateFolder as updateFolderFirestore, 
    deleteFolder as deleteFolderFirestore, 
    updateFolderOrder 
} from '../services/firestore.js';
import { useAuth } from './AuthContext.jsx';

const FoldersContext = createContext(null);

export function FoldersProvider({ children }) {
    const { user } = useAuth();
    
    // State
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedFolderId, setSelectedFolderId] = useState(null); // For filtering notes

    // Load folders from Firestore
    const loadFolders = useCallback(async () => {
        if (!user) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const userFolders = await getFolders(user.uid);
            setFolders(userFolders);
        } catch (err) {
            console.error('Failed to load folders:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Load folders when user changes
    useEffect(() => {
        if (user) {
            loadFolders();
        } else {
            setFolders([]);
            setLoading(false);
            setSelectedFolderId(null);
        }
    }, [user, loadFolders]);

    // Create a new folder
    const createFolder = useCallback(async (folderData) => {
        if (!user) return null;
        
        setError(null);
        
        try {
            const newFolder = await createFolderFirestore(user.uid, folderData);
            // Add to local state
            setFolders(prev => [...prev, newFolder]);
            return newFolder;
        } catch (err) {
            console.error('Failed to create folder:', err);
            setError(err.message);
            throw err;
        }
    }, [user]);

    // Update an existing folder
    const updateFolder = useCallback(async (folderId, updates) => {
        if (!user) return;
        
        setError(null);
        
        try {
            await updateFolderFirestore(user.uid, folderId, updates);
            // Update local state
            setFolders(prev => prev.map(folder => 
                folder.id === folderId 
                    ? { ...folder, ...updates, updatedAt: new Date() }
                    : folder
            ));
        } catch (err) {
            console.error('Failed to update folder:', err);
            setError(err.message);
            throw err;
        }
    }, [user]);

    // Delete a folder
    const deleteFolder = useCallback(async (folderId, deleteNotes = false) => {
        if (!user) return null;
        
        setError(null);
        
        try {
            const result = await deleteFolderFirestore(user.uid, folderId, deleteNotes);
            // Remove from local state
            setFolders(prev => prev.filter(folder => folder.id !== folderId));
            // Clear selection if this folder was selected
            if (selectedFolderId === folderId) {
                setSelectedFolderId(null);
            }
            return result;
        } catch (err) {
            console.error('Failed to delete folder:', err);
            setError(err.message);
            throw err;
        }
    }, [user, selectedFolderId]);

    // Reorder folders (for drag-drop)
    const reorderFolders = useCallback(async (orderedFolderIds) => {
        if (!user) return;
        
        setError(null);
        
        // Optimistically update local state
        const reorderedFolders = orderedFolderIds.map((id, index) => {
            const folder = folders.find(f => f.id === id);
            return folder ? { ...folder, order: index } : null;
        }).filter(Boolean);
        
        setFolders(reorderedFolders);
        
        try {
            await updateFolderOrder(user.uid, orderedFolderIds);
        } catch (err) {
            console.error('Failed to reorder folders:', err);
            setError(err.message);
            // Reload to get correct state
            loadFolders();
            throw err;
        }
    }, [user, folders, loadFolders]);

    // Select a folder for filtering (null = all notes)
    const selectFolder = useCallback((folderId) => {
        setSelectedFolderId(folderId);
    }, []);

    // Get folder by ID from local state
    const getFolderById = useCallback((folderId) => {
        return folders.find(folder => folder.id === folderId) || null;
    }, [folders]);

    // Get folders organized by parent/child hierarchy
    const getFolderHierarchy = useCallback(() => {
        const rootFolders = folders.filter(f => !f.parentId);
        const childMap = new Map();
        
        // Group children by parent
        folders.forEach(folder => {
            if (folder.parentId) {
                if (!childMap.has(folder.parentId)) {
                    childMap.set(folder.parentId, []);
                }
                childMap.get(folder.parentId).push(folder);
            }
        });
        
        // Build hierarchy
        const buildHierarchy = (parentFolders) => {
            return parentFolders.map(folder => ({
                ...folder,
                children: buildHierarchy(childMap.get(folder.id) || [])
            }));
        };
        
        return buildHierarchy(rootFolders);
    }, [folders]);

    // Memoized selected folder
    const selectedFolder = useMemo(() => {
        return selectedFolderId ? getFolderById(selectedFolderId) : null;
    }, [selectedFolderId, getFolderById]);

    const value = {
        folders,
        loading,
        error,
        selectedFolderId,
        selectedFolder,
        loadFolders,
        createFolder,
        updateFolder,
        deleteFolder,
        reorderFolders,
        selectFolder,
        getFolderById,
        getFolderHierarchy
    };

    return (
        <FoldersContext.Provider value={value}>
            {children}
        </FoldersContext.Provider>
    );
}

export function useFolders() {
    const context = useContext(FoldersContext);
    if (!context) {
        throw new Error('useFolders must be used within a FoldersProvider');
    }
    return context;
}